import type { VoidFunctionComponent } from 'react';

import { type IssueType, type LicensingIssueType } from '../../../../../../../shared/issuesV2';
import { TooltipBadge } from '../../../../../generic';
import Icon, { type IconClassName, icons } from '../../../../../generic/Icon';
import type { BadgeColor } from '../../../../../generic';

import styles from './DependencyLicenses.module.less';

type Props = {
  issueType?: IssueType
  isIgnored?: boolean;
  isScanned: boolean;
};

export const PolicyStatusBadge: VoidFunctionComponent<Props> = ({ issueType, isIgnored, isScanned }) => {
  const issueTypeMap: Record<LicensingIssueType, string> = {
    policy_flag: 'Flagged',
    policy_conflict: 'Denied',
    unlicensed_dependency: 'Unlicensed',
    unconcluded_dependency: 'Unconcluded',
    multi_license_concluded: 'Concluded (Multi)',
  };

  // When scanning is enabled: default to a checkmark on a green badge to represent an approved license.
  // When scanning is disabled: default to a checkmark on a gray badge to represent a stauts unknown license.
  let icon: IconClassName = isScanned ? icons.badgeCheck : icons.questionCircleO;
  let badgeColor: BadgeColor = isScanned ? 'dark-green' : 'dark-gray';

  switch (issueType) {
    case 'policy_flag':
      icon = icons.flagOutline;
      badgeColor = isIgnored ? 'light-yellow' : 'yellow';
      break;
    case 'policy_conflict':
      icon = icons.banOutline;
      badgeColor = isIgnored ? 'light-red' : 'dark-red';
      break;
    default:
      break;
  }

  const iconText = (
    (issueType && issueTypeMap[issueType as LicensingIssueType]) ||
    (isScanned ? 'Approved' : 'Unknown')
  );

  const tooltipText = isScanned ? 'Issue is ignored.' : 'License policy has not been applied';
  const shouldShowTooltip = isIgnored || (!isScanned && !issueType);

  return (
    <TooltipBadge
      color={badgeColor}
      size="micro"
      id={`policy-status-badge-${issueType}`}
      tooltip={tooltipText}
      showTooltip={shouldShowTooltip}
    >
      <div className={styles.policyStatusText}>
        <Icon icon={isIgnored ? icons.eyeSlash : icon} />
        {iconText}
      </div>
    </TooltipBadge>
  );
};
